// ============================================================================
// TASKDOCUMENTATIONPANEL.JS - COMPONENTE MODULAR PARA DOCUMENTACIÓN VINCULADA
// VERSIÓN CORREGIDA: Usa funciones del TaskStore en lugar de servicio directo
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import documentTaskService from '../services/DocumentTaskService';
import { smartOpenLink } from '../utils/smartLink';
import {
  BookOpen,
  Plus,
  RefreshCw,
  ExternalLink,
  Trash2,
  Search,
  X,
  FileText,
  Globe,
  File
} from 'lucide-react';
import useToast from '../services/useToast';
import '../styles/task-documentation-modal.css';

function TaskDocumentationPanel({ 
  taskId,
  documents,
  currentUser,
  authUser,
  loadingDocs,
  token
}) {
  const { showSuccess, showError, confirm } = useToast();
  const navigate = useNavigate();

  // Estados locales
  const [linkedDocs, setLinkedDocs] = useState([]);
  const [showDocSelector, setShowDocSelector] = useState(false);
  const docDialogRef = useRef(null);
  const [searchDocQuery, setSearchDocQuery] = useState('');
  const [loadingLinkedDocs, setLoadingLinkedDocs] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  
  // Resetear el flag hasLoaded cuando cambia taskId para permitir nueva carga
  useEffect(() => {
    setHasLoaded(false);
    setLinkedDocs([]);
  }, [taskId]);

  // Cargar documentos vinculados - SOLO UNA VEZ al montar o cuando cambia taskId
  useEffect(() => {
    let isMounted = true;

    const loadLinkedDocuments = async () => {
      if (!taskId) {
        console.log('No taskId, skipping load');
        return;
      }

      if (!token) {
        console.log('No token yet, skipping load');
        return;
      }

      if (hasLoaded || loadingLinkedDocs) {
        console.log('Already loaded or loading, skipping');
        return;
      }

      try {
        setLoadingLinkedDocs(true);
        console.log('Cargando documentos vinculados para tarea:', taskId);

        const linkedDocuments = await documentTaskService.getDocumentsByTask(taskId, token);

        if (isMounted) {
          // ✅ SIEMPRE establecer el array, aunque esté vacío
          setLinkedDocs(Array.isArray(linkedDocuments) ? linkedDocuments : []);
          setHasLoaded(true);
          console.log('Documentos vinculados cargados:', linkedDocuments?.length || 0);
        }
      } catch (error) {
        console.error('Error cargando documentos vinculados:', error);
        if (isMounted) {
          // ✅ En caso de error, establecer array vacío y marcar como cargado
          setLinkedDocs([]);
          setHasLoaded(true);
        }
      } finally {
        if (isMounted) {
          setLoadingLinkedDocs(false);
        }
      }
    };

    loadLinkedDocuments();

    return () => {
      isMounted = false;
    };
  }, [taskId, token]);
  /**
   * Obtener icono según el tipo de documento
   */
  const getDocTypeIcon = (tipo) => {
    switch (tipo?.toLowerCase()) {
      case 'documento':
      case 'google-docs':
        return <FileText size={14} className="doc-icon document" />;
      case 'enlace':
      case 'url':
        return <Globe size={14} className="doc-icon link" />;
      case 'pdf':
        return <File size={14} className="doc-icon pdf" />;
      default:
        return <BookOpen size={14} className="doc-icon document" />;
    }
  };

  /**
   * Filtrar documentos disponibles (excluir los ya vinculados)
   */
  const getFilteredAvailableDocs = () => {
    if (!documents) return [];

    return documents.filter(doc => {
      // Excluir documentos ya vinculados
      const isAlreadyLinked = linkedDocs.some(linked => linked.id === doc.id);
      if (isAlreadyLinked) return false;

      // Aplicar filtro de búsqueda
      if (searchDocQuery) {
        const searchLower = searchDocQuery.toLowerCase();
        return (
          doc.titulo?.toLowerCase().includes(searchLower) ||
          doc.descripcion?.toLowerCase().includes(searchLower) ||
          doc.nombre?.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });
  };

  /**
   * Agregar documentación a la tarea
   */
  const handleAddDocumentation = async (doc) => {
    console.log('Vinculando documento a tarea:', doc);
    
    const userId = currentUser?.id || authUser?.id;
    
    console.log('Datos disponibles:', { 
      taskId, 
      userId, 
      currentUser, 
      authUser 
    });
    
    if (!taskId || !userId) {
      console.error('Faltan datos para vincular documento:', { taskId, userId });
      showError('Faltan datos necesarios (task ID o user ID)');
      return;
    }

    try {
      console.log('Vinculando documento a tarea:', { 
        documentId: doc.id, 
        taskId, 
        userId 
      });

      const result = await documentTaskService.linkDocumentToTask(doc.id, taskId, userId, token);
      console.log('Resultado de linkDocumentToTask:', result);
      
      if (result) {
        setLinkedDocs(prev => [...prev, doc]);
        setShowDocSelector(false);
        setSearchDocQuery('');
        
        console.log('Documento vinculado exitosamente');
        showSuccess('Documento vinculado exitosamente');
      } else {
        console.error('Error en resultado:', result);
        showError('Error al vincular documento');
      }
    } catch (error) {
      console.error('Error vinculando documento:', error);
      showError(`Error al vincular documento: ${error.message}`);
    }
  };

  /**
   * Remover documentación de la tarea
   */
  const handleRemoveDocumentation = async (docId) => {
    const userConfirmed = await confirm(
      '¿Estás seguro de que quieres eliminar esta documentación vinculada?',
      {
        title: 'Confirmar desvinculación',
        confirmText: 'Desvincular',
        cancelText: 'Cancelar'
      }
    );

    if (!userConfirmed) return;

    if (!taskId) {
      console.error('No hay ID de tarea para desvincular documento');
      showError('No hay ID de tarea para desvincular documento');
      return;
    }

    try {
      console.log('Desvinculando documento de tarea:', { documentId: docId, taskId });

      const result = await documentTaskService.unlinkDocumentFromTask(docId, taskId, token);
      
      if (result) {
        setLinkedDocs(prev => prev.filter(doc => doc.id !== docId));
        
        console.log('Documento desvinculado exitosamente');
        showSuccess('Documento desvinculado correctamente');
      } else {
        showError('Error al desvincular documento');
      }
    } catch (error) {
      console.error('Error desvinculando documento:', error);
      showError(`Error al desvincular documento: ${error.message}`);
    }
  };

  // Clic en el título → abrir directo, sin modal intermedio: enlace externo en
  // pestaña nueva (o navegación SPA si es interno) y documento propio en su
  // vista del módulo de documentación (con preview de archivo si lo tiene).
  // Se pasa la ruta de origen en el state para que el botón de volver del
  // detalle devuelva a la tarea y no al dashboard de documentación.
  const handleDocumentTitleClick = (doc) => {
    if (doc.url) {
      smartOpenLink(doc.url, navigate);
    } else {
      // Si la URL lleva la referencia de la tarea (CERTI-123), el botón la usa.
      const ref = window.location.pathname.match(/\/detail\/([A-Za-z]+-\d+)/)?.[1];
      navigate(`/task/documentation/view/${doc.id}`, {
        state: {
          backTo: window.location.pathname,
          backLabel: ref ? `Volver a ${ref.toUpperCase()}` : 'Volver a la tarea'
        }
      });
    }
  };

  // Selector como diálogo: foco inicial en la caja y cierre con Escape
  useEffect(() => {
    if (!showDocSelector) return undefined;
    docDialogRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') setShowDocSelector(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showDocSelector]);

  return (
    <>
      {/* Panel de documentación */}
      <div className="task-documentation-sidebar-panel">
        <div className="info-panel-header">
          <h3 className="info-panel-title">
            <BookOpen className="section-icon" />
            Documentación
          </h3>
          {linkedDocs.length > 0 && (
            <span className="section-count">{linkedDocs.length}</span>
          )}
        </div>
        
        <div className="documentation-content">
          {loadingLinkedDocs ? (
            <div className="loading-docs">
              <RefreshCw size={16} className="loading-spinner" />
              <span>Cargando documentos...</span>
            </div>
          ) : linkedDocs.length > 0 ? (
            <>
              <div className="linked-docs-sidebar-list">
                {linkedDocs.map(doc => (
                  <div key={doc.id} className="linked-doc-sidebar-item">
                    <div className="linked-doc-sidebar-info">
                      <div className="linked-doc-sidebar-header">
                        {getDocTypeIcon(doc.tipo)}
                        <button
                          className="linked-doc-sidebar-title"
                          onClick={() => handleDocumentTitleClick(doc)}
                          title="Hacer clic para ver documento"
                        >
                          {doc.titulo || doc.nombre}
                        </button>
                      </div>
                      {doc.descripcion && (
                        <p className="linked-doc-sidebar-description">{doc.descripcion}</p>
                      )}
                    </div>
                    <div className="linked-doc-sidebar-actions">
                      {doc.url && (
                        <button
                          className="doc-action-btn view"
                          onClick={() => smartOpenLink(doc.url, navigate)}
                          title="Ver documento externo"
                        >
                          <ExternalLink size={14} />
                        </button>
                      )}
                      <button
                        className="doc-action-btn remove"
                        onClick={() => handleRemoveDocumentation(doc.id)}
                        title="Desvincular documento"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button
                className="btn-add-documentation"
                onClick={() => setShowDocSelector(true)}
                disabled={loadingDocs}
              >
                <Plus size={16} />
                Añadir documentación
              </button>
            </>
          ) : (
            <div className="docs-empty-card">
              <BookOpen className="docs-empty-icon" />
              <p className="docs-empty-title">Sin documentación</p>
              <small className="docs-empty-subtitle">Añade enlaces o documentos de referencia</small>
              <button
                className="docs-empty-add-btn"
                onClick={() => setShowDocSelector(true)}
                disabled={loadingDocs}
              >
                + Añadir
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal de selector de documentación */}
      {showDocSelector && (
        <div
          className="modal-overlay task-doc-modal-overlay u-sheet-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowDocSelector(false); }}
        >
          <div
            ref={docDialogRef}
            className="modal-content task-doc-modal u-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="task-doc-modal-title"
            tabIndex={-1}
          >
            <div className="documentation-selector">
              <div className="modal-header">
                <h3 id="task-doc-modal-title">Seleccionar documentación</h3>
                <button
                  type="button"
                  className="modal-close"
                  onClick={() => setShowDocSelector(false)}
                  aria-label="Cerrar"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="modal-body">
                {/* Búsqueda */}
                <div className="search-box">
                  <Search className="search-icon" />
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Buscar documentación..."
                    value={searchDocQuery}
                    onChange={(e) => setSearchDocQuery(e.target.value)}
                  />
                </div>

                {/* Lista de documentos disponibles */}
                <div className="available-docs-list">
                  {loadingDocs ? (
                    <div className="loading-docs">
                      <RefreshCw size={24} className="loading-spinner" />
                      <p>Cargando documentos...</p>
                    </div>
                  ) : getFilteredAvailableDocs().length > 0 ? (
                    getFilteredAvailableDocs().map(doc => (
                      <button
                        key={doc.id}
                        type="button"
                        className="available-doc-item"
                        onClick={() => handleAddDocumentation(doc)}
                        title={doc.descripcion || doc.titulo || doc.nombre}
                      >
                        <span className="doc-item-icon">{getDocTypeIcon(doc.tipo)}</span>
                        <span className="doc-item-title">{doc.titulo || doc.nombre}</span>
                        <span className="doc-item-add">
                          <Plus size={14} />
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="no-docs-found">
                      <Search size={32} />
                      <p>No se encontraron documentos</p>
                      <small>Intenta con otros términos de búsqueda</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

export default TaskDocumentationPanel;